import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider } from './context/AuthContext';
import PrivateRoute from './components/PrivateRoute';
import Login from './pages/Login';
import Register from './pages/Register';
import Dashboard from './pages/Dashboard';
import BugList from './pages/BugList';
import CreateBug from './pages/CreateBug';
import EditBug from './pages/EditBug';
import BugDetail from './pages/BugDetail';
import Applications from './pages/Applications';
import ApplicationDetail from './pages/ApplicationDetail';
import TesterApplications from './pages/TesterApplications';
import ApplicationTimeline from './pages/ApplicationTimeline';
import ManagerDashboard from './pages/ManagerDashboard';
import EmployeeList from './pages/EmployeeList';
import ProjectList from './pages/ProjectList';
import DeveloperReportBug from './pages/DeveloperReportBug';
import AccountSettings from './pages/AccountSettings';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import KanbanView from './pages/KanbanView';
import Inbox from './pages/Inbox';
import BugRedirect from './pages/BugRedirect';
import AppLayout from './components/AppLayout';

function App() {
  return (
    <AuthProvider>
      <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
        <div className="App">
          <Toaster
            position="top-right"
            containerStyle={{ top: 16, right: 16 }}
            toastOptions={{
              duration: 800,
              style: {
                background: '#363636',
                color: '#fff',
                fontSize: '14px',
                borderRadius: '8px',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
              },
              success: {
                duration: 800,
                iconTheme: {
                  primary: '#10b981',
                  secondary: '#fff',
                },
              },
              error: {
                duration: 1500,
                iconTheme: {
                  primary: '#ef4444',
                  secondary: '#fff',
                },
              },
            }}
          />


          <Routes>
            {/* Public Routes */}
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password/:resetToken" element={<ResetPassword />} />

            {/* Private Routes */}
            <Route
              path="/dashboard"
              element={
                <PrivateRoute>
                  <AppLayout>
                    <Dashboard />
                  </AppLayout>
                </PrivateRoute>
              }
            />
            <Route
              path="/kanban"
              element={
                <PrivateRoute>
                  <AppLayout>
                    <KanbanView />
                  </AppLayout>
                </PrivateRoute>
              }
            />
            <Route
              path="/bugs"
              element={
                <PrivateRoute>
                  <AppLayout>
                    <BugList />
                  </AppLayout>
                </PrivateRoute>
              }
            />
            <Route
              path="/inbox"
              element={
                <PrivateRoute>
                  <AppLayout>
                    <Inbox />
                  </AppLayout>
                </PrivateRoute>
              }
            />

            <Route
              path="/manager/kanban"
              element={
                <PrivateRoute allowedRoles={['manager']}>
                  <AppLayout>
                    <KanbanView />
                  </AppLayout>
                </PrivateRoute>
              }
            />

            <Route
              path="/bugs/new"
              element={
                <PrivateRoute allowedRoles={['tester']}>
                  <AppLayout>
                    <CreateBug />
                  </AppLayout>
                </PrivateRoute>
              }
            />
            <Route
              path="/developer/report-bug"
              element={
                <PrivateRoute allowedRoles={['developer']}>
                  <AppLayout>
                    <DeveloperReportBug />
                  </AppLayout>
                </PrivateRoute>
              }
            />
            <Route
              path="/bugs/:id/edit"
              element={
                <PrivateRoute allowedRoles={['tester', 'developer']}>
                  <AppLayout>
                    <EditBug />
                  </AppLayout>
                </PrivateRoute>
              }
            />
            <Route
              path="/bugs/:id"
              element={
                <PrivateRoute>
                  <AppLayout>
                    <BugDetail />
                  </AppLayout>
                </PrivateRoute>
              }
            />
            <Route
              path="/browse/:id"
              element={
                <PrivateRoute>
                  <AppLayout>
                    <BugDetail />
                  </AppLayout>
                </PrivateRoute>
              }
            />
            <Route
              path="/redirect/bug/:bugId"
              element={
                <PrivateRoute>
                  <AppLayout>
                    <BugRedirect />
                  </AppLayout>
                </PrivateRoute>
              }
            />
            <Route
              path="/applications"
              element={
                <PrivateRoute allowedRoles={['developer']}>
                  <AppLayout>
                    <Applications />
                  </AppLayout>
                </PrivateRoute>
              }
            />
            <Route
              path="/applications/:id"
              element={
                <PrivateRoute allowedRoles={['tester', 'developer']}>
                  <AppLayout>
                    <ApplicationDetail />
                  </AppLayout>
                </PrivateRoute>
              }
            />
            <Route
              path="/tester/applications"
              element={
                <PrivateRoute allowedRoles={['tester']}>
                  <AppLayout>
                    <TesterApplications />
                  </AppLayout>
                </PrivateRoute>
              }
            />
            <Route
              path="/view-timeline"
              element={
                <PrivateRoute allowedRoles={['tester', 'developer']}>
                  <AppLayout>
                    <ApplicationTimeline />
                  </AppLayout>
                </PrivateRoute>
              }
            />
            <Route
              path="/applications/:applicationId/timeline"
              element={
                <PrivateRoute>
                  <AppLayout>
                    <ApplicationTimeline />
                  </AppLayout>
                </PrivateRoute>
              }
            />
            <Route
              path="/timeline/:applicationId"
              element={
                <AppLayout>
                  <ApplicationTimeline />
                </AppLayout>
              }
            />
            <Route
              path="/account-settings"
              element={
                <PrivateRoute allowedRoles={['tester', 'developer']}>
                  <AppLayout>
                    <AccountSettings />
                  </AppLayout>
                </PrivateRoute>
              }
            />

            {/* Manager Routes */}
            <Route
              path="/manager/inbox"
              element={
                <PrivateRoute allowedRoles={['manager']}>
                  <AppLayout>
                    <Inbox />
                  </AppLayout>
                </PrivateRoute>
              }
            />

            <Route
              path="/manager/dashboard"
              element={
                <PrivateRoute allowedRoles={['manager']}>
                  <AppLayout>
                    <ManagerDashboard />
                  </AppLayout>
                </PrivateRoute>
              }
            />
            <Route
              path="/manager/employees"
              element={
                <PrivateRoute allowedRoles={['manager']}>
                  <AppLayout>
                    <EmployeeList />
                  </AppLayout>
                </PrivateRoute>
              }
            />
            <Route
              path="/manager/projects"
              element={
                <PrivateRoute allowedRoles={['manager']}>
                  <AppLayout>
                    <ProjectList />
                  </AppLayout>
                </PrivateRoute>
              }
            />
            <Route
              path="/manager/bugs"
              element={
                <PrivateRoute allowedRoles={['manager']}>
                  <AppLayout>
                    <BugList />
                  </AppLayout>
                </PrivateRoute>
              }
            />

            {/* Default Route */}
            <Route path="/" element={<Navigate to="/dashboard" />} />
            <Route path="*" element={<Navigate to="/dashboard" />} />
          </Routes>
        </div>
      </Router>
    </AuthProvider>
  );
}

export default App;
